import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Droplets,
  Plus,
  Calendar,
  Clock,
  MapPin,
  Car,
  Trash2,
  Edit,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';

const DEFAULT_AMOUNT = 10;

export default function DriverCarWash() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState({ total_entries: 0, total_amount: 0 });
  const [plates, setPlates] = useState([]);

  const now = new Date();
  const initialForm = () => ({
    vehicle_plate: user?.vehicle_plate || '',
    date: now.toISOString().slice(0, 10),
    time: now.toTimeString().slice(0, 5),
    where: '',
  });
  const [formData, setFormData] = useState(initialForm());

  useEffect(() => {
    fetchEntries();
    fetchStats();
    fetchPlates();
  }, []);

  const fetchPlates = async () => {
    try {
      const res = await axios.get(`${API}/vehicles/plates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlates(res.data || []);
    } catch (e) {
      console.error('Error fetching plates:', e);
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/car-wash`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(res.data || []);
    } catch (e) {
      console.error('Error fetching car wash entries:', e);
      toast.error('Failed to load car wash entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/car-wash/summary/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data || { total_entries: 0, total_amount: 0 });
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  };

  const resetForm = () => {
    setFormData(initialForm());
    setEditingEntry(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      vehicle_plate: entry.vehicle_plate || '',
      date: entry.date || '',
      time: entry.time || '',
      where: entry.where || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehicle_plate?.trim()) {
      toast.error('Number plate is required');
      return;
    }
    if (!formData.date) {
      toast.error('Date is required');
      return;
    }
    if (!formData.time) {
      toast.error('Time is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        vehicle_plate: formData.vehicle_plate.trim(),
        date: formData.date,
        time: formData.time,
        where: formData.where?.trim() || null,
      };
      if (editingEntry) {
        await axios.put(`${API}/car-wash/${editingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Car wash entry updated');
      } else {
        await axios.post(`${API}/car-wash`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Car wash logged');
      }
      setShowModal(false);
      resetForm();
      fetchEntries();
      fetchStats();
    } catch (err) {
      console.error('Save car wash error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Delete this car wash entry?')) return;
    try {
      await axios.delete(`${API}/car-wash/${entry.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Entry deleted');
      fetchEntries();
      fetchStats();
    } catch (e) {
      toast.error('Failed to delete entry');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="driver-car-wash-page">
      {/* Header */}
      <div className="bg-cyan-600 text-white px-6 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/driver')}
            className="text-white hover:bg-cyan-700"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Droplets className="h-6 w-6" /> Car Wash
            </h1>
            <p className="text-cyan-100 text-sm">Log your car washes</p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        {/* Add Button */}
        <Button
          onClick={openCreate}
          className="w-full mb-6 bg-cyan-600 hover:bg-cyan-700 py-6 text-lg"
          data-testid="add-car-wash-btn"
        >
          <Plus className="mr-2 h-5 w-5" />
          Add Car Wash Entry
        </Button>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="stat-total-entries">
            <p className="text-xs text-slate-500">Total entries</p>
            <p className="text-2xl font-bold text-slate-800">{stats.total_entries}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200" data-testid="stat-total-amount">
            <p className="text-xs text-slate-500">Total expense</p>
            <p className="text-2xl font-bold text-cyan-700">€{(stats.total_amount || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* Entries list */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-cyan-600" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-dashed border-slate-300">
            <Droplets className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">No car wash entries yet</p>
            <p className="text-xs text-slate-400">Tap "Add Car Wash Entry" to log your first one</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="car-wash-list">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="bg-white rounded-lg p-3 border border-slate-200 flex items-start justify-between gap-3"
                data-testid={`car-wash-row-${entry.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Car className="h-4 w-4 text-cyan-600" />
                    <span>{entry.vehicle_plate}</span>
                    <span className="ml-auto text-cyan-700 text-sm font-bold">€{(entry.amount || DEFAULT_AMOUNT).toFixed(2)}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(entry.date)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{entry.time}</span>
                    {entry.where && (
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{entry.where}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(entry)}
                    className="h-8 w-8 p-0 text-slate-600 hover:bg-slate-100"
                    data-testid={`edit-car-wash-${entry.id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(entry)}
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                    data-testid={`delete-car-wash-${entry.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(v) => { setShowModal(v); if (!v) resetForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Droplets className="h-5 w-5 text-cyan-600" />
              {editingEntry ? 'Edit Car Wash' : 'New Car Wash'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label>Number plate *</Label>
              {plates.length > 0 ? (
                <Select
                  value={formData.vehicle_plate}
                  onValueChange={(v) => setFormData({ ...formData, vehicle_plate: v })}
                >
                  <SelectTrigger data-testid="car-wash-plate-select">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {plates.map((p) => (
                      <SelectItem key={p.id} value={p.plate_number}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={formData.vehicle_plate}
                  onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value })}
                  placeholder="e.g. ABC-1234"
                  required
                  data-testid="car-wash-plate-input"
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="car-wash-date-input"
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                  data-testid="car-wash-time-input"
                />
              </div>
            </div>
            <div>
              <Label>Where (optional)</Label>
              <Input
                value={formData.where}
                onChange={(e) => setFormData({ ...formData, where: e.target.value })}
                placeholder="e.g. Shell car wash Athens"
                data-testid="car-wash-where-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1" disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-cyan-600 hover:bg-cyan-700" disabled={submitting} data-testid="save-car-wash-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingEntry ? 'Update' : 'Save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
